import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  Brain, Send, Trash2, Lightbulb, BarChart3, BookOpen, FileText,
  AlertTriangle, TrendingUp, History, Sparkles, ChevronRight, Loader2
} from 'lucide-react';

const PROMPT_ICONS = { reports: BarChart3, analytics: TrendingUp, journal: BookOpen, education: Lightbulb, audit: AlertTriangle };

export default function AIAssistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [history, setHistory] = useState([]);
  const [sessionId] = useState(() => `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/ai-assistant/prompts').then(r => setPrompts(r.data.prompts || [])).catch(() => {});
    api.get('/ai-assistant/history?limit=20').then(r => setHistory(r.data.conversations || [])).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const res = await api.post('/ai-assistant/chat', { message: msg, session_id: sessionId });
      setMessages(m => [...m, { role: 'assistant', text: res.data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.' }]);
      toast.error(e.response?.data?.detail || 'AI request failed');
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const [postingEntry, setPostingEntry] = useState(false);
  const [editEntry, setEditEntry] = useState(null); // { lines, narration, date }

  const clearHistory = async () => {
    try {
      await api.delete('/ai-assistant/history');
      setHistory([]);
      setMessages([]);
      toast.success('History cleared');
    } catch { toast.error('Failed to clear history'); }
  };

  const parseJournalEntry = (text) => {
    // Detect Dr/Cr lines in AI response
    const lines = [];
    const drPattern = /(?:Dr|Debit)\s+(.+?)(?:\s*[-—]\s*|\s+)([\d,]+(?:\.\d+)?)/gi;
    const crPattern = /(?:Cr|Credit)\s+(.+?)(?:\s*[-—]\s*|\s+)([\d,]+(?:\.\d+)?)/gi;
    let match;
    while ((match = drPattern.exec(text)) !== null) {
      lines.push({ account_name: match[1].trim(), debit: parseFloat(match[2].replace(/,/g, '')), credit: 0 });
    }
    while ((match = crPattern.exec(text)) !== null) {
      lines.push({ account_name: match[1].trim(), debit: 0, credit: parseFloat(match[2].replace(/,/g, '')) });
    }
    return lines.length >= 2 ? lines : null;
  };

  const postJournalEntry = async () => {
    if (!editEntry) return;
    setPostingEntry(true);
    try {
      const res = await api.post('/ai-assistant/post-entry', {
        lines: editEntry.lines, narration: editEntry.narration,
        date: editEntry.date || new Date().toISOString().split('T')[0],
        voucher_type: 'journal',
      });
      toast.success(res.data.message);
      setEditEntry(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post entry'); }
    setPostingEntry(false);
  };

  const formatMessage = (text) => {
    // Simple markdown-like formatting
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="px-1 py-0.5 bg-slate-100 rounded text-xs font-mono">$1</code>')
      .replace(/^### (.*$)/gm, '<h4 class="font-semibold text-slate-900 mt-3 mb-1">$1</h4>')
      .replace(/^## (.*$)/gm, '<h3 class="font-semibold text-slate-900 text-lg mt-3 mb-1">$1</h3>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="h-[calc(100vh-2rem)] flex gap-4" data-testid="ai-assistant-page">
      {/* Left Panel - History & Prompts */}
      <div className="w-64 shrink-0 flex flex-col gap-3">
        {/* Suggested Prompts */}
        <div className="bg-white border rounded-lg p-3 flex-1 overflow-y-auto">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Lightbulb size={13} /> Suggested Prompts
          </h3>
          <div className="space-y-1.5">
            {prompts.map((p, i) => {
              const Icon = PROMPT_ICONS[p.category] || Sparkles;
              return (
                <button key={i} onClick={() => sendMessage(p.text)} disabled={loading}
                  className="w-full text-left px-2.5 py-2 text-xs text-slate-600 hover:bg-orange-50 hover:text-orange-700 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid={`prompt-${i}`}>
                  <Icon size={12} className="shrink-0 text-slate-400" />
                  <span className="line-clamp-2">{p.text}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent History */}
        <div className="bg-white border rounded-lg p-3 max-h-64 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <History size={13} /> Recent
            </h3>
            {history.length > 0 && (
              <button onClick={clearHistory} className="text-slate-400 hover:text-red-500" data-testid="clear-history">
                <Trash2 size={12} />
              </button>
            )}
          </div>
          {history.length === 0 ? (
            <p className="text-[11px] text-slate-400">No conversations yet</p>
          ) : (
            <div className="space-y-1">
              {history.slice(-10).map((h, i) => (
                <button key={i} onClick={() => sendMessage(h.message)}
                  className="w-full text-left px-2 py-1.5 text-[11px] text-slate-500 hover:bg-slate-50 rounded truncate">
                  {h.message}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Panel */}
      <div className="flex-1 bg-white border rounded-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 px-5 py-3 flex items-center gap-3 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
            <Brain size={16} className="text-white" />
          </div>
          <div>
            <h2 className="text-white text-sm font-semibold">AI Financial Assistant</h2>
            <p className="text-slate-400 text-[10px]">Deep accounting analysis powered by Gemini</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mb-4">
                <Brain size={28} className="text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">AI Financial Assistant</h3>
              <p className="text-sm text-slate-500 max-w-md mb-6">
                Ask me anything about your accounts, generate journal entries, analyze financials, or detect anomalies.
              </p>
              <div className="grid grid-cols-2 gap-2 max-w-lg">
                {prompts.slice(0, 4).map((p, i) => (
                  <button key={i} onClick={() => sendMessage(p.text)}
                    className="text-left px-3 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-orange-50 hover:border-orange-200 transition-colors flex items-center gap-2">
                    <ChevronRight size={12} className="text-orange-500 shrink-0" />
                    <span>{p.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => {
            const parsedEntry = m.role === 'assistant' ? parseJournalEntry(m.text) : null;
            return (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                m.role === 'user'
                  ? 'bg-orange-600 text-white text-sm'
                  : 'bg-slate-50 border border-slate-200 text-slate-800 text-sm'
              }`}>
                {m.role === 'user' ? (
                  <p>{m.text}</p>
                ) : (
                  <>
                    <div className="prose prose-sm max-w-none prose-headings:text-slate-900 prose-strong:text-slate-900"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMessage(m.text)) }} />
                    {parsedEntry && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <button onClick={() => setEditEntry({ lines: parsedEntry, narration: m.text.split('\n')[0]?.replace(/[#*]/g, '').trim() || 'AI entry', date: new Date().toISOString().split('T')[0] })}
                          className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors flex items-center gap-1.5"
                          data-testid="post-entry-btn">
                          <BookOpen size={12} /> Post Entry to Ledger
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            );
          })}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-orange-600" />
                <span className="text-xs text-slate-500">Analyzing your data...</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 p-4 shrink-0">
          <div className="flex gap-2">
            <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Ask about financials, generate entries, analyze data..."
              className="form-input flex-1 text-sm" disabled={loading} data-testid="ai-input" />
            <button onClick={() => sendMessage()} disabled={loading || !input.trim()}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="ai-send">
              <Send size={14} /> Ask
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 text-center">AI analyzes your live ERP data. Verify important decisions independently.</p>
        </div>
      </div>

      {/* Right Panel - Quick Actions */}
      <div className="w-56 shrink-0 space-y-3">
        <div className="bg-white border rounded-lg p-3">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h3>
          <div className="space-y-1.5">
            {[
              { label: 'Generate Journal Entry', prompt: 'Create a journal entry for freight charges of Rs 25,000 + 18% GST from Delhi to Mumbai', icon: BookOpen },
              { label: 'Monthly P&L Summary', prompt: 'Generate monthly profit and loss summary', icon: BarChart3 },
              { label: 'Check for Errors', prompt: 'Check for any imbalanced journal entries or accounting errors', icon: AlertTriangle },
              { label: 'Revenue Analysis', prompt: 'Show revenue analysis by client with outstanding amounts', icon: TrendingUp },
              { label: 'Explain Accounting', prompt: 'Explain the difference between debit and credit in accounting', icon: FileText },
            ].map((action, i) => (
              <button key={i} onClick={() => sendMessage(action.prompt)} disabled={loading}
                className="w-full text-left px-2.5 py-2 text-xs text-slate-600 hover:bg-orange-50 hover:text-orange-700 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid={`quick-action-${i}`}>
                <action.icon size={12} className="shrink-0 text-slate-400" />
                {action.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
          <p className="text-[11px] text-orange-800 font-medium mb-1">Pro Tips</p>
          <ul className="text-[10px] text-orange-700 space-y-1 leading-relaxed">
            <li>Ask for journal entries with GST breakdowns</li>
            <li>Request comparisons between months</li>
            <li>Ask to detect anomalies in expenses</li>
            <li>Get explanations for balance differences</li>
          </ul>
        </div>
      </div>

      {/* Post Entry Confirmation Dialog */}
      {editEntry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="post-entry-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-5 border-b">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><BookOpen size={16} className="text-orange-600" /> Review & Post Journal Entry</h3>
              <p className="text-xs text-slate-500 mt-1">Review the AI-generated entry before posting to your ledger</p>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 font-medium">Date</label>
                  <input type="date" value={editEntry.date} onChange={e => setEditEntry({...editEntry, date: e.target.value})}
                    className="form-input text-xs mt-1" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-500 font-medium">Narration</label>
                  <input type="text" value={editEntry.narration} onChange={e => setEditEntry({...editEntry, narration: e.target.value})}
                    className="form-input text-xs mt-1" />
                </div>
              </div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 border-b">
                    <th className="px-3 py-2 text-left text-slate-500">Account</th>
                    <th className="px-3 py-2 text-right text-slate-500">Debit</th>
                    <th className="px-3 py-2 text-right text-slate-500">Credit</th>
                  </tr></thead>
                  <tbody>
                    {editEntry.lines.map((l, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2">
                          <input type="text" value={l.account_name} onChange={e => {
                            const lines = [...editEntry.lines]; lines[i] = {...lines[i], account_name: e.target.value};
                            setEditEntry({...editEntry, lines});
                          }} className="w-full text-xs border-0 bg-transparent focus:ring-0 p-0" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" value={l.debit || ''} onChange={e => {
                            const lines = [...editEntry.lines]; lines[i] = {...lines[i], debit: parseFloat(e.target.value) || 0};
                            setEditEntry({...editEntry, lines});
                          }} className="w-20 text-xs text-right border-0 bg-transparent focus:ring-0 p-0" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" value={l.credit || ''} onChange={e => {
                            const lines = [...editEntry.lines]; lines[i] = {...lines[i], credit: parseFloat(e.target.value) || 0};
                            setEditEntry({...editEntry, lines});
                          }} className="w-20 text-xs text-right border-0 bg-transparent focus:ring-0 p-0" />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-bold">
                      <td className="px-3 py-2 text-slate-700">Total</td>
                      <td className="px-3 py-2 text-right text-red-600">{editEntry.lines.reduce((s,l) => s + (l.debit || 0), 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-emerald-600">{editEntry.lines.reduce((s,l) => s + (l.credit || 0), 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {editEntry.lines.reduce((s,l) => s + (l.debit || 0), 0).toFixed(2) !== editEntry.lines.reduce((s,l) => s + (l.credit || 0), 0).toFixed(2) && (
                <p className="text-xs text-red-500 font-medium">Entry is not balanced. Debit must equal Credit.</p>
              )}
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setEditEntry(null)} className="px-4 py-2 text-xs text-slate-600 border rounded-md hover:bg-slate-50">Cancel</button>
              <button onClick={postJournalEntry} disabled={postingEntry}
                className="px-4 py-2 text-xs bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="confirm-post-entry">
                {postingEntry ? <><Loader2 size={12} className="animate-spin" /> Posting...</> : 'Confirm & Post Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
